import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  CircularProgress,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  IconButton,
  Tooltip,
  SelectChangeEvent,
  InputAdornment,
  Button,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  ShoppingCart as ShoppingCartIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
// import { db } from '../firebase/config';
// import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase-shim';
import * as indexedDBService from '../utils/indexedDBService';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useAnalytics from '../hooks/useAnalytics';

interface Transaction {
  id: string;
  type: 'Invoice' | 'Purchase' | 'Expense';
  description: string;
  party: string; // Customer/Vendor/Supplier
  amount: number;
  status: string;
  date: string;
  rawDate: Date;
}

const Transactions: React.FC = () => {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const analytics = useAnalytics();

  useEffect(() => {
    if (currentUser) {
      fetchAllTransactions();
    }
  }, [currentUser]);

  useEffect(() => {
    applyFilters();
  }, [transactions, searchQuery, typeFilter, statusFilter, startDate, endDate]);

  const fetchAllTransactions = async () => {
    if (!currentUser) return;

    setLoading(true);
    setRefreshing(true);
    try {
      // Fetch invoices using InvoiceService
      // We can dynamic import or use imports if we add them. 
      // But let's use indexedDBService for consistency with others here if we import it.
      // Actually InvoiceService is better.
      // But to save imports management in this big block, let's assume we imported InvoiceService and indexedDBService.

      // I will update imports in a separate call.

      const invoicesData = await indexedDBService.getAllItems<any>('invoices');
      const invoices = invoicesData.map(data => {
        const date = new Date(data.date || data.invoiceDate || Date.now());
        const partyName = typeof data.client === 'object' && data.client !== null
          ? (data.client.name || 'Client')
          : (data.client || data.clientName || data.clientDetails?.name || 'Client');
        return {
          id: data.id,
          type: 'Invoice' as const,
          description: data.invoiceNumber || `Invoice #${data.id.substring(0, 5)}`,
          party: partyName,
          amount: Number(data.amount || data.totalAmount || 0),
          status: data.status || 'Pending',
          date: date.toISOString().split('T')[0],
          rawDate: date
        };
      });

      // Fetch expenses
      const expensesData = await indexedDBService.getAllItems<any>('expenses');
      const expenses = expensesData.map(data => {
        const date = new Date(data.date || Date.now());
        const partyName = typeof data.vendor === 'object' && data.vendor !== null
          ? (data.vendor.name || 'Vendor')
          : (typeof data.payee === 'object' && data.payee !== null
            ? (data.payee.name || 'Vendor')
            : (data.vendor || data.payee || 'Vendor'));
        return {
          id: data.id,
          type: 'Expense' as const,
          description: data.title || data.description || data.category || `Expense`,
          party: partyName,
          amount: Number(data.amount || 0),
          status: data.status || 'Completed',
          date: date.toISOString().split('T')[0],
          rawDate: date
        };
      });

      // Fetch purchases
      const purchasesData = await indexedDBService.getAllItems<any>('purchases');
      const purchases = purchasesData.map(data => {
        const date = new Date(data.date || Date.now());
        const partyName = typeof data.vendor === 'object' && data.vendor !== null
          ? (data.vendor.name || 'Supplier')
          : (data.vendor || data.vendorName || 'Supplier');
        return {
          id: data.id,
          type: 'Purchase' as const,
          description: data.purchaseNumber || `Purchase #${data.id.substring(0, 5)}`,
          party: partyName,
          amount: Number(data.totalAmount || data.amount || 0),
          status: data.status || 'Pending',
          date: date.toISOString().split('T')[0],
          rawDate: date
        };
      });

      // Combine all transactions and sort by date (newest first)
      const allTransactions = [...invoices, ...expenses, ...purchases].sort((a, b) => {
        return b.rawDate.getTime() - a.rawDate.getTime();
      });

      setTransactions(allTransactions);
      setFilteredTransactions(allTransactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        transaction =>
          transaction.description.toLowerCase().includes(query) ||
          transaction.party.toLowerCase().includes(query) ||
          transaction.status.toLowerCase().includes(query)
      );
    }

    // Apply type filter
    if (typeFilter.length > 0) {
      filtered = filtered.filter(transaction => typeFilter.includes(transaction.type));
    }

    // Apply status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter(transaction => statusFilter.includes(transaction.status.toLowerCase()));
    }

    // Apply date range filter
    if (startDate) {
      filtered = filtered.filter(transaction => new Date(transaction.date) >= startDate);
    }

    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(transaction => new Date(transaction.date) <= endOfDay);
    }

    setFilteredTransactions(filtered);
  };

  const handleTypeFilterChange = (event: SelectChangeEvent<typeof typeFilter>) => {
    const value = event.target.value;
    setTypeFilter(typeof value === 'string' ? value.split(',') : value);

    // Track filter application in analytics
    if (value) {
      analytics.trackFilter('type', Array.isArray(value) ? value.join(',') : String(value));
    }
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<typeof statusFilter>) => {
    const value = event.target.value;
    setStatusFilter(typeof value === 'string' ? value.split(',') : value);

    // Track filter application in analytics
    if (value) {
      analytics.trackFilter('status', Array.isArray(value) ? value.join(',') : String(value));
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);

    // Track search in analytics
    if (event.target.value.trim()) {
      analytics.trackSearch(event.target.value, 'transactions');
    }
  };

  const handleRefresh = () => {
    fetchAllTransactions();
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setTypeFilter([]);
    setStatusFilter([]);
    setStartDate(null);
    setEndDate(null);
  };

  // Format currency values
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Get the appropriate icon for transaction type
  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'Invoice':
        return <ReceiptIcon fontSize="small" />;
      case 'Expense':
        return <PaymentsIcon fontSize="small" />;
      case 'Purchase':
        return <ShoppingCartIcon fontSize="small" />;
      default:
        return <ReceiptIcon fontSize="small" />;
    }
  };

  // Get the appropriate color for transaction type
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Invoice':
        return 'primary';
      case 'Expense':
        return 'error';
      case 'Purchase':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  // Get the appropriate color for transaction status
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'draft':
        return 'info';
      case 'deleted':
        return 'default';
      default:
        return 'default';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  return (
    <Box>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" fontWeight="bold">
          Transactions History
        </Typography>
        <Button
          variant="contained"
          startIcon={refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{
            backgroundImage: 'var(--primary-gradient)',
          }}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {/* Search and Filter Section */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ mr: 1 }}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              {showFilters && (
                <Button
                  variant="text"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              )}
            </Grid>

            {showFilters && (
              <>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="type-filter-label">Transaction Type</InputLabel>
                    <Select
                      labelId="type-filter-label"
                      multiple
                      value={typeFilter}
                      onChange={handleTypeFilterChange}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                              color={getTypeColor(value) as any}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      <MenuItem value="Invoice">Invoice</MenuItem>
                      <MenuItem value="Purchase">Purchase</MenuItem>
                      <MenuItem value="Expense">Expense</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      multiple
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                              color={getStatusColor(value) as any}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="overdue">Overdue</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="deleted">Deleted</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="From Date"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="To Date"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }
                        }
                      }}
                    />
                  </Grid>
                </LocalizationProvider>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)' }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : filteredTransactions.length === 0 ? (
            <Box sx={{ p: 5, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No transactions found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or search parameters
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Customer/Vendor</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.map((transaction) => (
                    <TableRow
                      key={`${transaction.type}-${transaction.id}`}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>{formatDate(transaction.date)}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getTransactionIcon(transaction.type)}
                          label={transaction.type}
                          color={getTypeColor(transaction.type) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell>{transaction.party}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                        {formatCurrency(transaction.amount)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={transaction.status}
                          color={getStatusColor(transaction.status) as any}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Transactions; 
